import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { Page, PageMetadata } from '@tran-monorepo/interfaces';
import { OrgUnit } from '@tran-monorepo/interfaces/applications';
import { EMPTY, Observable } from 'rxjs';
import { catchError, expand, finalize, takeWhile, tap } from 'rxjs/operators';
import { OrgUnitService } from '../../services/org-unit.service';
import { LoadAllOrgUnits, LoadMoreOrgUnits, LoadOrgUnits } from './org-unit.actions';

export interface OrgUnitStateModel {
    loading: boolean;
    orgUnits: OrgUnit[];
    metadata: PageMetadata | null;
    pageSize: number;
}

@State<OrgUnitStateModel>({
    name: 'orgUnits',
    defaults: {
        loading: false,
        orgUnits: [],
        metadata: null,
        pageSize: 20
    }
})
@Injectable()
export class OrgUnitState {
    private readonly orgUnitService = inject(OrgUnitService);

    @Selector()
    static orgUnits(state: OrgUnitStateModel): OrgUnit[] {
        return state.orgUnits;
    }

    @Selector()
    static loading(state: OrgUnitStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static metadata(state: OrgUnitStateModel): PageMetadata | null {
        return state.metadata;
    }

    @Action(LoadOrgUnits)
    load(ctx: StateContext<OrgUnitStateModel>, { payload: { pageSize } }: LoadOrgUnits): Observable<Page<OrgUnit>> {
        ctx.patchState({ loading: true, pageSize });

        return this.orgUnitService.getOrgUnits(0, pageSize).pipe(
            tap(({ items: orgUnits, metadata }) =>
                ctx.patchState({
                    orgUnits,
                    metadata
                })
            ),
            catchError(() => {
                ctx.patchState({ orgUnits: [], metadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(LoadMoreOrgUnits)
    loadMore(ctx: StateContext<OrgUnitStateModel>): Observable<Page<OrgUnit>> | void {
        const { loading, metadata, pageSize } = ctx.getState();
        if (loading || !metadata?.hasNextPage) {
            return;
        }

        const nextPage = (metadata?.currentPage ?? 0) + 1;
        ctx.patchState({ loading: true });

        return this.orgUnitService.getOrgUnits(nextPage, pageSize).pipe(
            tap(({ items: newOrgUnits, metadata }) =>
                ctx.setState(
                    patch<OrgUnitStateModel>({
                        orgUnits: append<OrgUnit>(newOrgUnits),
                        metadata
                    })
                )
            ),
            catchError(() => {
                ctx.patchState({ loading: false });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }

    @Action(LoadAllOrgUnits)
    loadAll(ctx: StateContext<OrgUnitStateModel>): Observable<void> {
        ctx.patchState({ loading: true, orgUnits: [], metadata: null });

        return ctx.dispatch(new LoadOrgUnits({ pageSize: 100 })).pipe(
            expand(() => {
                const { metadata } = ctx.getState();
                return metadata?.hasNextPage ? ctx.dispatch(new LoadMoreOrgUnits()) : EMPTY;
            }),
            takeWhile(() => {
                const { metadata } = ctx.getState();
                return metadata?.hasNextPage ?? false;
            }, true),
            catchError(() => {
                ctx.patchState({ loading: false, orgUnits: [], metadata: null });
                return EMPTY;
            }),
            finalize(() => ctx.patchState({ loading: false }))
        );
    }
}
